import { useRef } from "react";

type Collection<T> = {
  meta: {
    totalCount: number;
    nextPage?: number;
  };
  data: T[];
};

export type PaginatingEndpoint<T> = (p: Record<string, any>) => Collection<T>;

export default function usePages<Result>(
  call: PaginatingEndpoint<Result> = () => ({
    meta: { nextPage: undefined, totalCount: 0 },
    data: [],
  }),
  params: Record<string, any>,
  page: number,
  initialPage = 0,
  suspend = false
) {
  const responsesRef = useRef<Collection<Result>[]>([]);
  let previousResponses = responsesRef.current;
  let previousDataRef = useRef<Result[]>([]);
  let responses = [];
  let loading = false;
  let hasMore = true;
  let totalCount = 0;
  let recentPromise = null;

  try {
    // so you can pass a false-like value to skip calling
    if (call) {
      for (let pageIndex = initialPage; pageIndex <= page; pageIndex++) {
        const response = call({
          ...params,
          page: pageIndex + 1,
        });

        responses.push(response);

        responsesRef.current = responses;
        if (!response || !response.meta || !response.meta.nextPage) {
          hasMore = false;
          break;
        }
      }
    }
  } catch (e) {
    if (!(e instanceof Promise)) throw e;
    recentPromise = e;
    responses = responsesRef.current;
    loading = true;
  }

  let data: Result[] = [];
  if (
    responses.every((response, index) => response === previousResponses[index])
  ) {
    responses = previousResponses;
    data = previousDataRef.current;
  } else {
    data = responses.map((r) => r?.data).reduce((a, b) => a.concat(b), []);
    previousDataRef.current = data;
  }

  if (responses[0]) {
    totalCount = responses[0].meta ? responses[0].meta.totalCount : 0;
  }

  if (suspend && recentPromise) {
    throw recentPromise;
  }

  return {
    loading,
    totalCount,
    hasMore,
    data,
  };
}
